// src/components/appeals/AppealsHeader.tsx

import React, { useMemo } from 'react';
import { isEmpty } from 'lodash';
import type { MultiValue } from 'react-select';

import { cn } from '@xtnd/web/shared/utils';
import { Button, Checkbox, Icon, Input, Select } from '@repo/ui';
import { useDarkMode } from '@xtnd/web/shared/hooks/useDarkMode';
import { GroupByOption, DeadlineTier } from '@repo/types';
import type { SelectOption } from '@repo/ui';

import { AppealGroup } from '../../types';
import { useAppealStatusOptions } from '../../hooks';
import ArReportingReload from '../common/ArReportingReload';
import BulkAssignBar from './BulkAssignBar';

const GROUP_BY_OPTIONS = [
  { label: 'No Grouping', value: GroupByOption.NONE },
  { label: 'Group by Client', value: GroupByOption.CLIENT },
  { label: 'Group by Responsible Attorney', value: GroupByOption.RESPONSIBLE },
];

const DEADLINE_FILTER_OPTIONS = [
  { label: 'Tier 1 (Required within working week)', value: DeadlineTier.TIER_1 },
  { label: 'Tier 2 (Required with 1-2 weeks)', value: DeadlineTier.TIER_2 },
  { label: 'Tier 3 (Required within 30 days)', value: DeadlineTier.TIER_3 },
];

/**
 * Props for the AppealsHeader component.
 * It combines props for both global and local filter views.
 */
interface AppealsHeaderProps {
  groupBy: GroupByOption;
  rowSelected: AppealGroup | null;
  clientDeadlineTier: DeadlineTier | '';
  handleClientDeadlineTierChange?: (value: DeadlineTier | '') => void;

  // Global Filter Props (when rowSelected is null)
  search?: string;
  statusSelected?: SelectOption[];
  assignedTo?: SelectOption[] | null;
  isCheckedClosedInvoices?: boolean;
  isLoading?: boolean;
  refetch?: () => void;
  handleSearch?: (value: string) => void;
  handleSelectedStatus?: (value: MultiValue<SelectOption> | null) => void;
  handleSelectedGroupBy?: (value: GroupByOption) => void;
  handleCheckedClosedInvoices?: () => void;
  handleFiltersByAssignedTo?: (value: SelectOption[] | null) => void;

  // Local Filter Props (when rowSelected is NOT null)
  searchLocal?: string;
  statusSelectedLocal?: SelectOption[];
  assignedToLocal?: SelectOption[] | null;
  isLoadingLocal?: { search: boolean; status: boolean };
  isCheckedClosedInvoicesSelected?: boolean;
  handleSearchLocal?: (value: string) => void;
  handleSelectedStatusLocal?: (selected: MultiValue<SelectOption> | null) => Promise<void>;
  onBackToClients?: () => void;
  handleCheckedClosedInvoicesSelected?: () => void;
  handleFilterByAssignedToLocal?: (value: SelectOption[] | null) => void;

  // Bulk Assign Props
  selectedKeys?: string[];
  onAssigneeChange?: (value: string) => void;
  isBulkAssigning?: boolean;

  // Clerk Users Options (passed from parent to avoid duplicate API calls)
  clerkUsersOptions?: SelectOption[];
}

/**
 * @description Renders the header section with search, filters, and grouping options.
 * It switches between the global view and the client detail view.
 */
const AppealsHeader: React.FC<AppealsHeaderProps> = ({
  groupBy,
  rowSelected,
  clientDeadlineTier,
  handleClientDeadlineTierChange = () => {},

  // Global
  search = '',
  statusSelected = [],
  assignedTo,
  isCheckedClosedInvoices = false,
  isLoading = false,
  refetch = () => {},
  handleSearch = () => {},
  handleSelectedStatus = () => {},
  handleSelectedGroupBy = () => {},
  handleCheckedClosedInvoices = () => {},
  handleFiltersByAssignedTo = () => {},

  // Local
  searchLocal = '',
  statusSelectedLocal = [],
  assignedToLocal,
  isLoadingLocal = { search: false, status: false },
  isCheckedClosedInvoicesSelected = false,
  handleSearchLocal = () => {},
  handleSelectedStatusLocal = async () => {},
  onBackToClients = () => {},
  handleCheckedClosedInvoicesSelected = () => {},
  handleFilterByAssignedToLocal = () => {},

  // Bulk Assign
  selectedKeys = [],
  onAssigneeChange = () => {},
  isBulkAssigning = false,

  clerkUsersOptions = [],
}) => {
  const { isDark } = useDarkMode();
  const selectStatusOptions = useAppealStatusOptions();

  const assignedFilterOptions = useMemo<SelectOption[]>(
    () => [{ label: 'Unassigned', value: 'unassigned' }, ...clerkUsersOptions],
    [clerkUsersOptions]
  );

  // --- CLIENT DETAIL VIEW (rowSelected is NOT null) ---
  if (!isEmpty(rowSelected)) {
    return (
      <div className="flex flex-col justify-between w-full gap-1">
        <div className="flex justify-start items-center gap-4">
          <Button
            variant="ghost"
            className="flex items-center gap-1 font-medium text-xtnd-dark dark:text-xtnd-light"
            onClick={onBackToClients}
          >
            <Icon name="FaAngleLeft" /> Back to{' '}
            {groupBy === GroupByOption.CLIENT ? 'Clients' : 'Attorneys'}
          </Button>
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            {rowSelected.groupName || ''} - Invoices
          </div>
        </div>
        {/* <div className="flex justify-between w-full gap-x-2 items-center"> */}
        {/* Filter UI - always visible */}
        <div className="flex flex-col lg:flex-row flex-wrap justify-between w-full gap-2 items-start lg:items-center">
          {/* Local Search Input */}
          <div className="relative flex-1 duration-300">
            <Icon
              name="HiSearch"
              size="sm"
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
            />
            <Input
              value={searchLocal}
              variant="outlined"
              onChange={e => handleSearchLocal(e.target.value)}
              placeholder="Search matter or invoice"
              className="flex lg:w-[20rem] px-3 py-2 pl-9 border border-xtnd-light dark:border-xtnd-dark focus:ring-offset-xtnd-blue focus:ring-transparent focus:border-xtnd-blue dark:focus:border-xtnd-blue"
            />
          </div>
          {/* Local Status Filter */}
          <Select<SelectOption>
            value={statusSelectedLocal}
            onChange={handleSelectedStatusLocal}
            options={selectStatusOptions}
            placeholder="Filter status"
            multiple={true}
            isDarkMode={isDark}
            isLoading={isLoadingLocal.status}
            disabled={isLoadingLocal.status}
            className="text-sm w-full sm:w-[12rem] xl:w-[20rem] 2xl:w-[30rem]"
            classNamePrefix="react-select"
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            clearable={true}
            searchable={false}
            menuPlacement="auto"
            menuPosition="fixed"
          />
          <Select
            options={assignedFilterOptions}
            value={assignedToLocal || []}
            isLoading={isLoadingLocal.status}
            disabled={isLoadingLocal.status}
            onChange={value => handleFilterByAssignedToLocal?.(value as SelectOption[] | null)}
            placeholder="Filter assigned"
            clearable={true}
            multiple={true}
            className="text-sm w-full sm:w-[8rem] xl:w-[13rem]"
            menuPlacement="auto"
            menuPosition="fixed"
            searchable={true}
          />
          <Select
            options={DEADLINE_FILTER_OPTIONS}
            value={clientDeadlineTier || ''}
            isLoading={isLoadingLocal.status}
            disabled={isLoadingLocal.status}
            onChange={handleClientDeadlineTierChange as any}
            placeholder="Filter deadline"
            clearable={true}
            className="text-sm w-full sm:w-[8rem] xl:w-[10rem] 2xl:w-[11rem]"
            menuPlacement="auto"
            menuPosition="fixed"
          />
        </div>
        {/* Bulk Assign UI - shown when items are selected */}
        <BulkAssignBar
          selectedCount={selectedKeys.length}
          assigneeOptions={clerkUsersOptions}
          onAssigneeChange={onAssigneeChange}
          isBulkAssigning={isBulkAssigning}
        />
      </div>
    );
  }

  // --- GLOBAL LIST VIEW (rowSelected is null) ---

  let searchPlaceholder = 'Search matter or invoice';
  if (groupBy === GroupByOption.CLIENT) {
    searchPlaceholder = 'Search client';
  }

  const toolbarActionClassName = cn('flex flex-wrap gap-2 lg:gap-x-4 items-center', {
    'justify-start lg:justify-end w-full lg:w-auto': groupBy === GroupByOption.NONE,
    'justify-end': groupBy === GroupByOption.CLIENT,
  });

  return (
    <div className="flex flex-col justify-between">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light mb-4">
          Invoice Appeals Management
        </h3>
        <ArReportingReload isLoading={isLoading} refetch={refetch} />
      </div>

      <div className="flex flex-col lg:flex-row flex-wrap justify-between w-full gap-2 items-start lg:items-center">
        {/* Global Search Input */}
        <div className="relative w-full lg:flex-1 duration-300 lg:max-w-md">
          <Icon
            name="HiSearch"
            size="sm"
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
          />
          <Input
            value={search}
            variant="outlined"
            onChange={e => handleSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex lg:w-[20rem] px-3 py-1 pl-9 !border border-xtnd-light dark:border-xtnd-dark focus:ring-offset-xtnd-blue focus:ring-transparent focus:border-xtnd-blue dark:focus:border-xtnd-blue"
          />
        </div>
        {/* Filter UI - always visible */}
        <div className={toolbarActionClassName}>
          {groupBy === GroupByOption.NONE && (
            <div className="min-w-0 w-full sm:w-auto sm:min-w-[12rem] xl:min-w-[20rem]">
              {/* Global Status Filter */}
              <Select<SelectOption>
                value={statusSelected}
                onChange={handleSelectedStatus}
                options={selectStatusOptions}
                placeholder="Filter by status"
                multiple={true}
                isDarkMode={isDark}
                isLoading={isLoading}
                disabled={isLoading}
                classNamePrefix="react-select"
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                clearable={true}
                searchable={false}
                menuPlacement="auto"
                menuPosition="fixed"
                className="text-sm w-full sm:w-[12rem] xl:w-[20rem] 2xl:w-[30rem]"
              />
            </div>
          )}
          {/* Group By Selector */}
          <Select
            options={GROUP_BY_OPTIONS}
            value={groupBy}
            isLoading={isLoading}
            disabled={isLoading}
            onChange={handleSelectedGroupBy as any}
            className="text-sm w-full sm:w-[8rem] 2xl:w-[10rem]"
          />
          {groupBy === GroupByOption.NONE && (
            <>
              <Select
                options={assignedFilterOptions}
                value={assignedTo || []}
                isLoading={isLoading}
                disabled={isLoading}
                onChange={value => handleFiltersByAssignedTo?.(value as SelectOption[] | null)}
                placeholder="Filter assigned"
                clearable={true}
                multiple={true}
                className="text-sm w-full sm:w-[8rem] xl:w-[13rem]"
                menuPlacement="auto"
                menuPosition="fixed"
                searchable={true}
              />
              <Select
                options={DEADLINE_FILTER_OPTIONS}
                value={clientDeadlineTier || ''}
                isLoading={isLoading}
                disabled={isLoading}
                onChange={handleClientDeadlineTierChange as any}
                placeholder="Filter deadline"
                clearable={true}
                className="text-sm w-full sm:w-[8rem] xl:w-[10rem] 2xl:w-[11rem]"
              />
            </>
          )}
          {/* Include Closed Checkbox */}
          <label className="flex gap-x-2 justify-center items-center cursor-pointer whitespace-nowrap">
            <Checkbox
              checked={isCheckedClosedInvoices}
              disabled={isLoading}
              onChange={handleCheckedClosedInvoices}
            />
            <span className="text-sm text-xtnd-dark dark:text-xtnd-light">Incl. Closed</span>
          </label>
        </div>
      </div>
      {/* Bulk Assign UI - shown when items are selected */}
      <BulkAssignBar
        selectedCount={selectedKeys.length}
        assigneeOptions={clerkUsersOptions}
        onAssigneeChange={onAssigneeChange}
        isBulkAssigning={isBulkAssigning}
      />
    </div>
  );
};

export default AppealsHeader;
